import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './WinePage.css';
import { Helmet } from 'react-helmet-async';

// Import all images for checksum paths
import roséWine from './assets/corina-rainer-Sv-eBEGLYhM-unsplash.jpg';
import whiteWine from './assets/thomas-martinsen-Eo9ZNtEUH_Y-unsplash.jpg';
import redWine from './assets/marios-gkortsilas-hvtZ0nP3ZEU-unsplash.jpg';
import sparklingWine from './assets/alaksiej-carankievic-ssD_xI_Gtjw-unsplash.jpg';
import dessertWine from './assets/kelly-visel-_5fxVH4dqUo-unsplash.jpg';
import fortifiedWine from './assets/kevin-kelly-PPneSBqfCCU-unsplash.jpg';
import orangeWine from './assets/victoria-shes-8OpyEpJVgiQ-unsplash.jpg';
import naturalWine from './assets/ambitious-studio-rick-barrett-WT_r3dUwx6I-unsplash.jpg';
import blushWine from './assets/kevin-kelly-PPneSBqfCCU-unsplash.jpg';
import flowerWine from './assets/photo-nic-3etN9PY6iNw-unsplash.jpg';

const wineBackgrounds = {
    "Rosé Wine": roséWine,
    "White Wine": whiteWine,
    "Red Wine": redWine,
    "Sparkling Wine": sparklingWine,
    "Dessert Wine": dessertWine,
    "Fortified Wine": fortifiedWine,
    "Orange Wine": orangeWine,
    "Natural Wine": naturalWine,
    "Blush Wine": blushWine,
    "Flower Wine": flowerWine,
};

const WinePage = () => {
    const { dishname } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { wineResult } = location.state || {};

    const [backgroundImage, setBackgroundImage] = useState('');
    const [fadeClass, setFadeClass] = useState('fade-in'); // For CSS transitions
    const [isLoading, setIsLoading] = useState(false);
    const [loadingButton, setLoadingButton] = useState(null); // Track which button is loading
    const [upvoteSuccess, setUpvoteSuccess] = useState(false);
    const [disputeSuccess, setDisputeSuccess] = useState(false);
    const [upvoteError, setUpvoteError] = useState('');
    const [disputeError, setDisputeError] = useState('');
    const [newWineResult, setNewWineResult] = useState(wineResult);

    useEffect(() => {
        if (!dishname) {
            navigate('/');
        }

        if (typeof wineResult === 'undefined') {
            const fetchAdditionalDishInfo = async () => {
                try {
                    const response = await fetch(`https://api.winetodish.com/dish/${encodeURIComponent(dishname)}`);
                    if (!response.ok) {
                        throw new Error('Failed to fetch additional dish information.');
                    }
                    const data = await response.json();
                    setNewWineResult(data);
                } catch (error) {
                    console.error('Error fetching additional dish info:', error);
                }
            };

            fetchAdditionalDishInfo();
        }
    }, [dishname, navigate, wineResult]);

    useEffect(() => {
        if (newWineResult) {
            const image = wineBackgrounds[newWineResult.type];
            if (image) {
                setFadeClass('fade-out');
                setTimeout(() => {
                    setBackgroundImage(image);
                    setFadeClass('fade-in');
                }, 500); // Match transition duration
            }
        }
    }, [newWineResult]);

    useEffect(() => {
        const loadAds = () => {
            if (window.adsbygoogle) {
                try {
                    window.adsbygoogle.push({});
                } catch (e) {
                    console.error('AdSense error:', e);
                }
            }
        };

        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9576361864687114';
        script.crossOrigin = 'anonymous';
        script.onload = loadAds;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const handleNewRecommendation = async () => {
        try {
            setIsLoading(true);
            setLoadingButton('newRecommendation');
            setUpvoteSuccess(false);
            setDisputeSuccess(false);
            const response = await fetch(
                `https://api.winetodish.com/dish/${encodeURIComponent(dishname)}?exclude=${encodeURIComponent(newWineResult.grape_variety)}`,
                { method: 'GET' }
            );
            if (!response.ok) {
                throw new Error('Failed to fetch a new recommendation.');
            }
            const data = await response.json();
            setNewWineResult(data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
            setLoadingButton(null);
        }
    };

    const handleUpvote = async () => {
        try {
            setIsLoading(true);
            setLoadingButton('upvote');
            setUpvoteError('');
            const response = await fetch('https://api.winetodish.com/upvote', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dishname, grape_variety: newWineResult.grape_variety }),
            });
            if (!response.ok) {
                throw new Error('Failed to upvote.');
            }
            setUpvoteSuccess(true);
        } catch (error) {
            console.error(error);
            setUpvoteError('Failed to upvote. Please try again.');
        } finally {
            setIsLoading(false);
            setLoadingButton(null);
        }
    };

    const handleDispute = async () => {
        try {
            setIsLoading(true);
            setLoadingButton('dispute');
            setDisputeError('');
            const response = await fetch('https://api.winetodish.com/dispute', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dishname, grape_variety: newWineResult.grape_variety }),
            });
            if (!response.ok) {
                throw new Error('Failed to dispute.');
            }
            setDisputeSuccess(true);
        } catch (error) {
            console.error(error);
            setDisputeError('Failed to dispute. Please try again.');
        } finally {
            setIsLoading(false);
            setLoadingButton(null);
        }
    };

    if (!newWineResult) {
        navigate('/');
        return null;
    }

    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": newWineResult?.grape_variety || "Wine Recommendation",
        "description": newWineResult?.description || "Find the best wine pairing recommendations.",
        "brand": {
            "@type": "Brand",
            "name": "WineToDish"
        },
        "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "price": "19.99", // Example price, can be made dynamic
            "availability": "https://schema.org/InStock",
            "seller": {
                "@type": "Organization",
                "name": "WineToDish"
            }
        }
    };

    return (
        <>
        <Helmet>
           <title>{`${newWineResult?.grape_variety} Wine Pairing | WineToDish`}</title>
            <meta name="description" content="Discover the best wine pairings with dishes. Expert recommendations for reds, whites, rosés, and more." />
            <link rel="canonical" href={`https://winetodish.com/dish/${encodeURIComponent(dishname)}`} />
            <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        </Helmet>
        <div
            className={`container-fluid ${fadeClass}`}
            style={{ backgroundImage: `url(${backgroundImage})` }}
        >
            <div className="wine-container">
                <div className="wine-title"><h1>{newWineResult.grape_variety}</h1></div>
                <div className="wine-details">
                        <h2>Type:</h2>
                        <p>{newWineResult.type}</p>
                        <h2>Region:</h2>
                        <p>{newWineResult.region}</p>
                        <h2>Description:</h2>
                        <p>{newWineResult.description}</p>
                        <h2>Serving Recommendations:</h2>
                        <p>{newWineResult.serving}</p>
                </div>

                <div className="adsense-mid-content" style={{ margin: '20px 0' }}>
                </div>

<a
    href="#"
    className="btn btn-primary btn-action"
    onClick={(e) => {
        e.preventDefault(); // Prevent full page reload
        handleNewRecommendation();
    }}
    disabled={isLoading ? "true" : undefined} 
    aria-busy={isLoading ? "true" : "false"}
>
    {loadingButton === 'newRecommendation' ? (
        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
    ) : (
        'Suggest Another Wine'
    )}
</a>

<ins className="adsbygoogle"
     style={{ display: 'block', height: '50%' }}
     data-ad-format="fluid"
     data-ad-layout-key="-i0-3+1f-3d+2z"
     data-ad-client="ca-pub-9576361864687114"
     data-ad-slot="3574819515"></ins>


<button
    className="btn btn-secondary btn-action"
    onClick={handleUpvote}
    disabled={isLoading || upvoteSuccess}
    aria-busy={isLoading ? "true" : "false"}
    aria-pressed={upvoteSuccess ? "true" : "false"}
    aria-label={upvoteSuccess ? "Upvoted" : "Upvote this post"}
>
    {loadingButton === 'upvote' ? (
        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
    ) : upvoteSuccess ? (
        <>
            <ion-icon name="heart-sharp" aria-hidden="true"></ion-icon>
            <span> Upvoted</span>
        </>
    ) : (
        'Upvote'
    )}
</button>
                {upvoteError && <div className="error-text">{upvoteError}</div>}

<button
    className="btn btn-secondary btn-action"
    onClick={handleDispute}
    disabled={isLoading || disputeSuccess}
    aria-busy={isLoading ? "true" : "false"}
    aria-pressed={disputeSuccess ? "true" : "false"}
    aria-label={disputeSuccess ? "Already disputed as incorrect" : "Dispute this as incorrect"}
>
    {loadingButton === 'dispute' ? (
        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
    ) : disputeSuccess ? (
        <>
            <ion-icon name="checkmark-sharp" aria-hidden="true"></ion-icon>
            <span> Disputed</span>
        </>
    ) : (
        'Dispute as Incorrect'
    )}
</button>
                {disputeError && <div className="error-text">{disputeError}</div>}

<a
    className="new-search-link"
    href="/"
    onClick={(e) => {
        e.preventDefault(); // Prevent full reload if JavaScript is enabled
        navigate('/');
    }}
>
    Start a New Search
</a>
            </div>
        </div>
        </>
    );
};

export default WinePage;
